import { type ReactNode } from 'react';
import { useApp } from '@/store/app';
import { Star, Share2 } from 'lucide-react';
import { Button } from './Button';
import { Tooltip } from './Tooltip';
import { cn } from '@/lib/cn';
import { copyToClipboard } from '@/lib/utils';
import { toast } from 'sonner';

interface ToolHeaderProps {
  toolId: string;
  title: string;
  description?: ReactNode;
  badges?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function ToolHeader({ toolId, title, description, badges, actions, className }: ToolHeaderProps) {
  const { favorites, toggleFavorite } = useApp();
  const isFav = favorites.includes(toolId);

  const handleShare = async () => {
    const url = `${window.location.origin}/tool/${toolId}`;
    const ok = await copyToClipboard(url);
    if (ok) toast.success('Link copied');
  };

  return (
    <div className={cn('mb-6', className)}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
            {badges}
          </div>
          {description && (
            <div className="text-sm text-fg-muted mt-1.5 max-w-2xl">{description}</div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {actions}
          <Tooltip content="Share link">
            <Button variant="ghost" size="icon-sm" onClick={handleShare}>
              <Share2 size={14} />
            </Button>
          </Tooltip>
          <Tooltip content={isFav ? 'Remove favorite' : 'Add favorite'}>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => {
                toggleFavorite(toolId);
                toast.success(isFav ? 'Removed from favorites' : 'Added to favorites');
              }}
            >
              <Star size={14} className={cn(isFav && 'fill-warning text-warning')} />
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
